import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { isRealtimeEvent, REALTIME_EVENT_NAME } from 'fym-shared/src/music24/realtimeEvents';
import { fymAPI } from '../../redux/api/fymAPI';
import { useUserToken } from '../../redux/slices/appInfo/hooks';
import { refreshSessionToken } from '../app/session/refreshSessionToken';
import { createRealtimeSocket } from './createRealtimeSocket';
import { RealtimeContextValue, RealtimeListener } from './RealtimeContext';
import { realtimeEventTags, realtimeTagTypes } from './realtimeEventTags';
import { createThrottledTagInvalidation } from './throttledTagInvalidation';

/** The wait before a new connection after the server closed the socket (the token ended, a workspace change). */
export const SERVER_CLOSE_RECONNECT_MS = 2000;
/** The wait before a new try after the server refused the session and a refresh did not help. */
export const REFUSED_RETRY_MS = 60 * 1000;
/** The wait before a new try after the server could not check the session. */
export const UNAVAILABLE_RETRY_MS = 15 * 1000;
/** At most one session refresh per this time because of a refused handshake. */
export const REFRESH_GUARD_MS = 60 * 1000;

/** The refusals that a new session token can repair (`RealtimeHandshakeError`). */
const REFRESH_REASONS = ['expired', 'unauthorized', 'no_workspace'];

/**
 * The one realtime socket of the tab. It lives while the app holds a session token.
 *
 * - A new token (the hourly refresh, a workspace switch) closes the socket and opens one with the
 *   new token. The server decides the room from the token, so this is also how a tab changes room.
 * - A refused handshake refreshes the session once, like a 401 of the API. The new token then
 *   opens a new socket.
 * - A socket that the server closed connects again after a short wait, and the server checks the
 *   token again.
 * - Each event invalidates its cache tags (`realtimeEventTags`); a tag of `THROTTLED_REALTIME_TAGS`
 *   at most one time in its wait (`throttledTagInvalidation`). A connection after the first one
 *   invalidates every tag of the map, because an event may have gone by during the break.
 *
 * With no socket the app works as before: each poll site keeps its own interval
 * (`useRealtimePollInterval`).
 */
export function useRealtimeSocket(): RealtimeContextValue {
  const dispatch = useDispatch();
  const userToken = useUserToken();
  const [isConnected, setConnected] = useState(false);
  const listeners = useRef(new Set<RealtimeListener>());
  const hasBeenConnected = useRef(false);
  const lastRefreshAt = useRef(0);

  const subscribe = useCallback((listener: RealtimeListener) => {
    listeners.current.add(listener);
    return () => {
      listeners.current.delete(listener);
    };
  }, []);

  useEffect(() => {
    if (userToken === null || userToken === undefined || userToken === '') {
      return undefined;
    }
    const token = userToken;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let stopped = false;
    const socket = createRealtimeSocket(() => token);
    // A tag of `THROTTLED_REALTIME_TAGS` goes to the cache at most one time in its wait.
    const eventTags = createThrottledTagInvalidation((tags) => {
      dispatch(fymAPI.util.invalidateTags(tags));
    });

    const connectLater = (delayMs: number) => {
      clearTimeout(timer);
      timer = setTimeout(() => {
        if (!stopped) {
          socket.connect();
        }
      }, delayMs);
    };

    socket.on('connect', () => {
      setConnected(true);
      if (hasBeenConnected.current) {
        dispatch(fymAPI.util.invalidateTags(realtimeTagTypes));
      }
      hasBeenConnected.current = true;
    });

    socket.on('disconnect', (reason: string) => {
      setConnected(false);
      // Socket.IO connects again by itself after a network break, but not after the server
      // closed the socket.
      if (reason === 'io server disconnect') {
        connectLater(SERVER_CLOSE_RECONNECT_MS);
      }
    });

    socket.on('connect_error', (error: Error) => {
      setConnected(false);
      if (socket.active) {
        // A network error: Socket.IO tries again by itself.
        return;
      }
      if (!REFRESH_REASONS.includes(error.message)) {
        connectLater(UNAVAILABLE_RETRY_MS);
        return;
      }
      if (Date.now() - lastRefreshAt.current < REFRESH_GUARD_MS) {
        connectLater(REFUSED_RETRY_MS);
        return;
      }
      lastRefreshAt.current = Date.now();
      // `no_workspace` does not say that the token is dead: HTTP accepts a session with no workspace.
      refreshSessionToken(token, dispatch, { unauthorized: error.message !== 'no_workspace' })
        .then((outcome) => {
          // `refreshed` with a new token runs this effect again with that token. `signedOut`
          // removes the token, and the effect ends the socket.
          if (outcome.kind === 'failed' || (outcome.kind === 'refreshed' && outcome.token === token)) {
            connectLater(REFUSED_RETRY_MS);
          }
        })
        .catch(console.error);
    });

    socket.on(REALTIME_EVENT_NAME, (payload: unknown) => {
      if (!isRealtimeEvent(payload)) {
        return;
      }
      eventTags.invalidate(realtimeEventTags(payload));
      listeners.current.forEach((listener) => listener(payload));
    });

    return () => {
      stopped = true;
      clearTimeout(timer);
      eventTags.cancel();
      socket.removeAllListeners();
      socket.disconnect();
      setConnected(false);
    };
  }, [dispatch, userToken]);

  return useMemo(() => ({ isConnected, subscribe }), [isConnected, subscribe]);
}
